import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, Chip, Card, CardContent, Grid, Backdrop, CircularProgress, TextField, IconButton, useTheme, ToggleButton, ToggleButtonGroup, Tooltip, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TableRowsIcon from '@mui/icons-material/TableRows';
import { Add, CheckCircle, Cancel, Visibility, SmartToy, Search, Edit, Delete, Business, LocationOn } from '@mui/icons-material';
import { DataTableWithStaticSchema } from 'components/DataTable/DataTableWithStaticSchema';
import { CompanyDialog } from './CompanyDialog';
import { CompanyViewDialog } from './CompanyViewDialog';
import { CompanyBotManager } from './CompanyBotManager';
import { GET_ALL_COMPANIES_URL, API_BASE_URL, CREATE_COMPANY_URL } from 'envConfig';
import { Company, ProductPlan } from 'modules/Interfaces/Company';
import { useSelector } from 'react-redux';
import { RootState } from 'state/store';
import { Logger } from 'utils/Logger';
import { apiClient } from 'utils/ApiClient';

// Helper function to format date
const formatDate = (dateString: string): string => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch (error) {
    return 'Invalid Date';
  }
};

// Helper function to check if plan is active
const isPlanActive = (startDate: string, endDate: string, companyName?: string, systemCompanyName?: string): boolean => {
  if (companyName && systemCompanyName && companyName === systemCompanyName) {
    return true;
  }
  
  if (!startDate || !endDate) return false;
  
  try {
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    return now >= start && now <= end;
  } catch (error) {
    return false;
  }
};

// Helper function to check if a company has users
const checkCompanyHasUsers = async (companyId: string): Promise<boolean> => {
  try {
    // Use ApiClient instead of fetch (automatically includes Authorization header)
    const result = await apiClient.get(`/user/all/${companyId}`);
    const users = result.data || result || [];
    
    Logger.debug('Companies.tsx', `Company ${companyId} has ${users.length} users`);
    return users.length > 0;
  } catch (error) {
    Logger.error('Companies.tsx', `Error checking users for company ${companyId}: ${error}`);
    return false; // Assume no users if there's an error
  }
};

// Schema object for company data - only including the columns we want to display
const companySchema = {
  paths: {
    _id: { instance: 'String' },
    name: { instance: 'String' },
    city: { instance: 'String' },
    country: { instance: 'String' },
    productPlans: { instance: 'Array' },
    botManager: { instance: 'String' }
  }
};

// Custom column renderers for better display
const columnRenderers = {
  _id: (row: Company) => {
    return (
      <Box sx={{ fontFamily: 'monospace', fontSize: '0.875rem', color: 'text.secondary' }}>
        {row._id}
      </Box>
    );
  },
  botManager: (row: Company) => {
    const value = row.configurations?.defaultBotManager || 'Platform Managed';
    const isClientManaged = (value || '').toLowerCase() === 'clientmanaged';
    return (
      isClientManaged ? (
        <Chip
          label={value}
          size="small"
          variant="outlined"
          sx={(theme) => ({
            color: theme.palette.warning.main,
            borderColor: theme.palette.warning.main,
          })}
        />
      ) : (
        <Chip label={value} color="info" size="small" variant="filled" />
      )
    );
  },
  productPlans: (row: Company) => {
    if (!row.productPlans || row.productPlans.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }}>
          No plans
        </Box>
      );
    }
    
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {row.productPlans.map((plan: ProductPlan, index: number) => {
          const key = plan._id || `${plan.module}-${plan.licenseType}-${index}`;
          const isActive = isPlanActive(plan.startedAt, plan.endsAt, row.name, 'SHARADTECH');
          
          return (
            <Card 
              key={key} 
              variant="outlined" 
              sx={{ 
                minWidth: 200,
                borderColor: isActive ? 'success.main' : 'error.main',
                '&:hover': {
                  boxShadow: 2,
                  borderColor: isActive ? 'success.dark' : 'error.dark'
                }
              }}
            >
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Grid container spacing={1}>
                  {/* Module */}
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Module:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {plan.module || 'N/A'}
                    </Typography>
                  </Grid>
                  
                  {/* License Type */}
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      License:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {plan.licenseType || 'N/A'}
                    </Typography>
                  </Grid>
                  
                  {/* Start Date */}
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Start Date:
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(plan.startedAt)}
                    </Typography>
                  </Grid>
                  
                  {/* End Date */}
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      End Date:
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(plan.endsAt)}
                    </Typography>
                  </Grid>
                  
                  {/* Status */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      {isActive ? (
                        <CheckCircle sx={{ color: 'success.main', fontSize: 16 }} />
                      ) : (
                        <Cancel sx={{ color: 'error.main', fontSize: 16 }} />
                      )}
                      <Chip
                        label={isActive ? 'ACTIVE' : 'INACTIVE'}
                        size="small"
                        color={isActive ? 'success' : 'error'}
                        variant="outlined"
                        sx={{ 
                          fontSize: '0.7rem',
                          height: 20,
                          '& .MuiChip-label': { px: 1 }
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    );
  }
};

export const Companies: React.FC = () => {
  const systemCompanyName = 'SHARADTECH';

  // Function to handle view company details
  const [viewLoading, setViewLoading] = useState<boolean>(false);
  const handleView = async (record: Company): Promise<void> => {
    try {
      setViewLoading(true);
      // Fetch full company details to include nested configurations like defaultBotManager
      const result = await apiClient.get(`/company/${record._id}`);
      const companyData = (result as any).data || result;
      setCompanyToView(companyData as Company);
    } catch (error) {
      Logger.error('Companies.tsx', `Failed to load company details for view: ${error}`);
      // Fallback to the provided record so the dialog still opens
      setCompanyToView(record);
    } finally {
      setViewDialogOpen(true);
      setViewLoading(false);
    }
  };

  // Custom actions function for the data table
  const customActions = (record: Company) => {
    const actions = [];
    
    // Add View button
    actions.push({
      label: 'View Details',
      onClick: () => handleView(record),
      color: 'info' as const,
      icon: <Visibility />
    });
    // Add Manage Bot button
    actions.push({
      label: 'Manage Bot',
      onClick: () => handleManageBot(record),
      color: 'secondary' as const,
      icon: <SmartToy />
    });
    
    return actions;
  };
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [viewDialogOpen, setViewDialogOpen] = useState<boolean>(false);
  const [currentCompany, setCurrentCompany] = useState<Partial<Company> | null>(null);
  const [companyToView, setCompanyToView] = useState<Company | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [editLoading, setEditLoading] = useState<boolean>(false);
  const [companiesWithUsers, setCompaniesWithUsers] = useState<Set<string>>(new Set());
  const [checkingUsers, setCheckingUsers] = useState<boolean>(false);
  const [deleteBlockedReason, setDeleteBlockedReason] = useState<string>('');
  const [botManagerDialogOpen, setBotManagerDialogOpen] = useState<boolean>(false);
  const [companyForBot, setCompanyForBot] = useState<Company | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<string>('asc');
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  const theme = useTheme();

  const VIEWMODE_STORAGE_KEY = 'companies-view-mode';
  const [viewMode, setViewMode] = useState<'tile' | 'table'>(() => {
    const stored = localStorage.getItem(VIEWMODE_STORAGE_KEY);
    return stored === 'table' ? 'table' : 'tile';
  });
  const [companiesData, setCompaniesData] = useState<Company[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const TILE_PAGE_SIZE = 24;
  const [tileVisibleCount, setTileVisibleCount] = useState(TILE_PAGE_SIZE);

  // Get current user from Redux store
  const currentUser = useSelector((state: RootState) => state.authentication.user);

  const fetchCompaniesData = useCallback(async () => {
    setCompaniesLoading(true);
    try {
      const data = await apiClient.get(GET_ALL_COMPANIES_URL.replace(API_BASE_URL, ''));
      const companies = Array.isArray(data) ? data : (data.data || []);
      setCompaniesData(companies);
    } catch (error) {
      Logger.error('Failed to fetch companies for tile view:', error);
    } finally {
      setCompaniesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompaniesData();
  }, [refreshTrigger, fetchCompaniesData]);

  const filteredCompanies = useMemo(() => {
    let filtered = companiesData;
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter((c) => {
        const name = (c.name || '').toLowerCase();
        const city = (c.city || '').toLowerCase();
        const country = (c.country || '').toLowerCase();
        const email = (c.companyEmail || '').toLowerCase();
        return name.includes(q) || city.includes(q) || country.includes(q) || email.includes(q);
      });
    }
    filtered = [...filtered].sort((a, b) => {
      let comparison = 0;
      if (sortField === 'name') {
        comparison = (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase());
      } else if (sortField === 'updatedAt') {
        const dateA = new Date(a.updatedAt || a.createdAt || 0).getTime();
        const dateB = new Date(b.updatedAt || b.createdAt || 0).getTime();
        comparison = dateA - dateB;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    return filtered;
  }, [companiesData, searchTerm, sortField, sortOrder]);

  useEffect(() => {
    setTileVisibleCount(TILE_PAGE_SIZE);
  }, [searchTerm, companiesData, sortField, sortOrder]);

  const paginatedCompanies = useMemo(() => filteredCompanies.slice(0, tileVisibleCount), [filteredCompanies, tileVisibleCount]);
  const hasMoreTiles = tileVisibleCount < filteredCompanies.length;

  const handleEdit = async (record: Company): Promise<void> => {
    try {
      setEditLoading(true);
      // Fetch full company details to include nested items like brand logos
      const result = await apiClient.get(`/company/${record._id}`);
      const companyData = (result as any).data || result;
      setCurrentCompany(companyData as Company);
    } catch (error) {
      Logger.error('Companies.tsx', `Failed to load company details for edit: ${error}`);
      // Fallback to provided record so dialog still opens
      setCurrentCompany(record);
    } finally {
      setIsEditing(true);
      setDialogOpen(true);
      setEditLoading(false);
    }
  };

  const handleManageBot = (record: Company): void => {
    setCompanyForBot(record);
    setBotManagerDialogOpen(true);
  };

  const handleDelete = async (record: Company): Promise<void> => {
    try {
      setCheckingUsers(true);
      
      // Check if company has users before allowing delete
      const hasUsers = await checkCompanyHasUsers(record._id);
      if (hasUsers) {
        setCompaniesWithUsers(prev => new Set(prev).add(record._id));
        setDeleteBlockedReason('hasUsers');
      } else {
        setDeleteBlockedReason('');
      }
      
      setCompanyToDelete(record);
      setDeleteDialogOpen(true);
    } catch (error) {
      Logger.error('Companies.tsx', `Error checking users for company ${record._id}: ${error}`);
      // If we can't check users, allow the delete but warn the user
      setDeleteBlockedReason('checkFailed');
      setCompanyToDelete(record);
      setDeleteDialogOpen(true);
    } finally {
      setCheckingUsers(false);
    }
  };

  // Function to check if delete should be disabled
  const isDeleteDisabled = (record: Company): boolean => {
    // Disable delete for current user's company
    if (currentUser?.companyId === record._id) {
      return true;
    }
    
    // Disable delete for system company
    if (record.name === systemCompanyName) {
      return true;
    }
    
    // Disable delete if company has users
    if (companiesWithUsers.has(record._id)) {
      return true;
    }
    
    // Disable delete while checking for users
    if (checkingUsers) {
      return true;
    }
    
    return false;
  };

  // Function to provide tooltip for disabled delete button
  const deleteDisabledTooltip = (record: Company): string => {
    if (currentUser?.companyId === record._id) {
      return "Cannot delete your own company";
    }
    
    if (record.name === systemCompanyName) {
      return "Cannot delete the system company";
    }
    
    if (companiesWithUsers.has(record._id)) {
      return "Cannot delete company that has users. Please remove all users first.";
    }
    
    if (checkingUsers) {
      return "Checking if company has users...";
    }
    
    return "Delete company";
  };

  const openNewCompanyDialog = (): void => {
    setCurrentCompany({
      name: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      country: '',
      zip: '',
      companyEmail: '',
      website: '',
      taxcode: '',
      companyPhone: '',
      registrationNumber: '',
      productPlans: [],
      configurations: {
        defaultBotManager: ''
      }
    });
    setIsEditing(false);
    setDialogOpen(true);
  };

  const confirmDelete = async (): Promise<void> => {
    if (!companyToDelete) return;

    // Double-check protection for system company
    if (companyToDelete.name === systemCompanyName) {
      Logger.error('Companies.tsx', `Attempted to delete system company: ${companyToDelete.name}`);
      setDeleteDialogOpen(false);
      setCompanyToDelete(null);
      setDeleteBlockedReason('');
      return;
    }

    // Prevent deletion if company has users
    if (deleteBlockedReason === 'hasUsers') {
      setDeleteDialogOpen(false);
      setCompanyToDelete(null);
      setDeleteBlockedReason('');
      return;
    }

    try {
      setDeleteLoading(true);
      
      // Use ApiClient instead of fetch (automatically includes Authorization header)
      await apiClient.delete(`/company/${companyToDelete._id}`);

      Logger.debug('Companies.tsx', `Company deleted successfully: ${companyToDelete._id}`);
      
      // Close dialog and refresh table
      setDeleteDialogOpen(false);
      setCompanyToDelete(null);
      setDeleteBlockedReason('');
      setRefreshTrigger(prev => prev + 1);
      
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      Logger.error('Companies.tsx', `Failed to delete company: ${errorMessage}`);
      // TODO: Show error message to user
    } finally {
      setDeleteLoading(false);
    }
  };

  const closeDeleteDialog = (): void => {
    setDeleteDialogOpen(false);
    setCompanyToDelete(null);
    setDeleteBlockedReason('');
  };

  const closeDialog = (): void => {
    setDialogOpen(false);
    setCurrentCompany(null);
    setIsEditing(false);
  };

  const closeViewDialog = (): void => {
    setViewDialogOpen(false);
    setCompanyToView(null);
  };

  const closeBotManagerDialog = (): void => {
    setBotManagerDialogOpen(false);
    setCompanyForBot(null);
  };

  const saveCompany = async (): Promise<void> => {
    if (!currentCompany) return;

    try {
      setSaveLoading(true);
      
      const url = isEditing ? `${API_BASE_URL}/company/${currentCompany._id}` : CREATE_COMPANY_URL;
      const method = isEditing ? 'PUT' : 'POST';
      
      // Prepare payload based on whether we're editing or creating
      let payloadToSend: any; // Use any for simplicity since we're sending to API
      
      if (isEditing) {
        // For editing, keep the _id but remove it from the top level
        const { _id, ...payload } = currentCompany;
        payloadToSend = payload;
      } else {
        // For new companies, remove _id from both company and product plans
        const { _id, ...companyWithoutId } = currentCompany;
        payloadToSend = {
          ...companyWithoutId,
          productPlans: companyWithoutId.productPlans?.map((plan: ProductPlan) => {
            const { _id: planId, ...planWithoutId } = plan;
            return planWithoutId;
          }) || []
        };
      }
      
      // Use ApiClient instead of fetch (automatically includes Authorization header)
      if (isEditing) {
        await apiClient.put(`/company/${currentCompany._id}`, {
          ...payloadToSend,
          modifiedBy: currentUser?.email,
        });
      } else {
        await apiClient.post('/company/new', {
          ...payloadToSend,
          createdBy: currentUser?.email,
        });
      }

      Logger.debug('Companies.tsx', `Company ${isEditing ? 'updated' : 'created'} successfully: ${JSON.stringify(currentCompany)}`);
      
      // Close dialog and refresh table
      closeDialog();
      setRefreshTrigger(prev => prev + 1);
      
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      Logger.error('Companies.tsx', `Failed to ${isEditing ? 'update' : 'create'} company: ${errorMessage}`);
      // TODO: Show error message to user
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <Box pl={{ xs: 1.25, md: 2.5 }} pt={1} width="100%">
      <Backdrop open={editLoading} sx={{ zIndex: (theme) => theme.zIndex.modal + 1, color: '#fff' }}>
        <CircularProgress color="inherit" />
        <Typography sx={{ ml: 2 }}>Loading company details...</Typography>
      </Backdrop>
      <Backdrop open={viewLoading} sx={{ zIndex: (theme) => theme.zIndex.modal + 1, color: '#fff' }}>
        <CircularProgress color="inherit" />
        <Typography sx={{ ml: 2 }}>Loading company details...</Typography>
      </Backdrop>
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: { xs: 1, md: 2 },
          alignItems: 'center',
          mb: 3,
          p: 1.5,
          borderRadius: 2,
          backgroundColor: (theme) =>
            theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.06)',
        }}
      >
        {/* Mobile: Search toggle */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
          <IconButton
            color="secondary"
            onClick={() => setMobileSearchOpen(prev => !prev)}
            size="large"
            sx={{
              border: '1px solid',
              borderColor: 'secondary.main',
              '&:hover': { backgroundColor: 'action.hover', borderColor: 'secondary.dark' },
            }}
          >
            <Search />
          </IconButton>
        </Box>

        {/* Desktop: Search Box */}
        <Box sx={{ display: { xs: 'none', md: 'block' }, flexGrow: 1 }}>
          <TextField
            label="Search Companies"
            variant="outlined"
            color="secondary"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flexGrow: 1, minWidth: '200px' }}
          />
        </Box>

        {/* Spacer for mobile */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, flexGrow: 1 }} />

        {/* Desktop: Sort Controls */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'row', gap: 1, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel>Sort By</InputLabel>
            <Select value={sortField} label="Sort By" onChange={(e) => setSortField(e.target.value as string)}>
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="updatedAt">Last Updated On</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 100 }} size="small">
            <InputLabel>Order</InputLabel>
            <Select value={sortOrder} label="Order" onChange={(e) => setSortOrder(e.target.value as string)}>
              <MenuItem value="asc">ASC</MenuItem>
              <MenuItem value="desc">DSC</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Desktop: View Mode Toggle + New Company */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_e, newMode) => {
              if (newMode !== null) {
                setViewMode(newMode);
                localStorage.setItem(VIEWMODE_STORAGE_KEY, newMode);
              }
            }}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                border: '1px solid',
                borderColor: 'divider',
                '&.Mui-selected': {
                  backgroundColor: 'secondary.main',
                  color: 'secondary.contrastText',
                  '&:hover': { backgroundColor: 'secondary.dark' },
                },
              },
            }}
          >
            <ToggleButton value="tile" aria-label="Tile view">
              <Tooltip title="Tile View"><ViewModuleIcon fontSize="small" /></Tooltip>
            </ToggleButton>
            <ToggleButton value="table" aria-label="Table view">
              <Tooltip title="Table View"><TableRowsIcon fontSize="small" /></Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<Add />}
            onClick={openNewCompanyDialog}
            size="small"
            sx={{ whiteSpace: 'nowrap' }}
          >
            New Company
          </Button>
        </Box>

        {/* Mobile: New Company icon button */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
          <IconButton
            color="secondary"
            onClick={openNewCompanyDialog}
            size="large"
            sx={{
              border: '1px solid',
              borderColor: 'secondary.main',
              '&:hover': { backgroundColor: 'action.hover', borderColor: 'secondary.dark' },
            }}
          >
            <Add />
          </IconButton>
        </Box>
      </Box>

      {/* Mobile: Expandable search field */}
      {mobileSearchOpen && (
        <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
          <TextField
            label="Search Companies"
            variant="outlined"
            color="secondary"
            size="small"
            fullWidth
            autoFocus
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <FormControl sx={{ flex: 1 }} size="small">
              <InputLabel>Sort By</InputLabel>
              <Select value={sortField} label="Sort By" onChange={(e) => setSortField(e.target.value as string)}>
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="updatedAt">Last Updated On</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ flex: 1 }} size="small">
              <InputLabel>Order</InputLabel>
              <Select value={sortOrder} label="Order" onChange={(e) => setSortOrder(e.target.value as string)}>
                <MenuItem value="asc">ASC</MenuItem>
                <MenuItem value="desc">DSC</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      )}

      {/* ─── TABLE VIEW ─── */}
      {viewMode === 'table' && (
        <DataTableWithStaticSchema
          dataUrl={GET_ALL_COMPANIES_URL}
          schemaObject={companySchema}
          columnsToExclude={[]}
          onEdit={handleEdit}
          onDelete={handleDelete}
          columnRenderers={columnRenderers}
          refreshTrigger={refreshTrigger}
          isDeleteDisabled={isDeleteDisabled}
          deleteDisabledTooltip={deleteDisabledTooltip}
          customActions={customActions}
          columnsWithDisabledFilter={['productPlans']}
          externalSearchQuery={searchTerm}
          hideSearch
        />
      )}

      {/* ─── TILE VIEW ─── */}
      {viewMode === 'tile' && (
        companiesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : filteredCompanies.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography color="text.secondary">
              {searchTerm ? 'No companies match your search.' : 'No companies found.'}
            </Typography>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={2.5}>
            <Box
              display="flex"
              flexWrap="wrap"
              gap={2.5}
              sx={{ justifyContent: { xs: 'center', md: 'flex-start' } }}
            >
              {paginatedCompanies.map((company) => {
                const logoSrc = theme.palette.mode === 'dark'
                  ? (company.brand?.logo?.dark || company.brand?.logo?.light)
                  : (company.brand?.logo?.light || company.brand?.logo?.dark);
                const location = [company.city, company.country].filter(Boolean).join(', ');
                const hasActivePlan = company.productPlans?.some(p => isPlanActive(p.startedAt, p.endsAt, company.name, systemCompanyName));

                return (
                  <Box
                    key={company._id}
                    onClick={() => handleView(company)}
                    sx={{
                      width: 260,
                      height: 200,
                      borderRadius: 2.5,
                      overflow: 'hidden',
                      position: 'relative',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      cursor: 'pointer',
                      flexShrink: 0,
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover .tile-overlay': { height: 130 },
                      '&:hover .tile-details': { opacity: 1 },
                      '&:hover .tile-name-short': { opacity: 0, height: 0, lineHeight: 0 },
                      '@media (hover: none)': {
                        '& .tile-overlay': { height: 130 },
                        '& .tile-details': { opacity: 1 },
                        '& .tile-name-short': { opacity: 0, height: 0, lineHeight: 0 },
                      },
                    }}
                  >
                    {/* Gradient background with logo and location */}
                    <Box sx={{
                      position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5,
                    }}>
                      {logoSrc ? (
                        <Box
                          component="img"
                          src={logoSrc}
                          alt={company.name}
                          sx={{ maxWidth: 100, maxHeight: 72, objectFit: 'contain', borderRadius: 1 }}
                        />
                      ) : (
                        <Box sx={{
                          width: 72, height: 72, borderRadius: 2,
                          bgcolor: 'rgba(255,255,255,0.2)', border: '2px solid rgba(255,255,255,0.4)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <Business sx={{ fontSize: 36, color: '#fff' }} />
                        </Box>
                      )}
                      {location && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <LocationOn sx={{ fontSize: 14, color: 'rgba(255,255,255,0.7)' }} />
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'rgba(255,255,255,0.7)', textAlign: 'center' }} noWrap>
                            {location}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Top-right: Status badge */}
                    <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
                      <Chip
                        label={hasActivePlan ? 'ACTIVE' : 'INACTIVE'}
                        size="small"
                        color={hasActivePlan ? 'success' : 'error'}
                        sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, color: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }}
                      />
                    </Box>

                    {/* Bottom overlay */}
                    <Box
                      className="tile-overlay"
                      sx={{
                        position: 'absolute', bottom: 0, left: 0, right: 0,
                        overflow: 'hidden',
                        background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.72) 50%, rgba(0,0,0,0.25) 100%)',
                        transition: 'height 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                        height: 40, px: 1.5,
                        display: 'flex', flexDirection: 'column', justifyContent: 'flex-start',
                      }}
                    >
                      {/* Company name — always visible */}
                      <Typography variant="subtitle2" noWrap className="tile-name-short" sx={{
                        color: '#fff', fontWeight: 700, fontSize: '0.82rem',
                        lineHeight: '40px', textShadow: '0 1px 3px rgba(0,0,0,0.4)', flexShrink: 0,
                        transition: 'opacity 0.2s ease, height 0.2s ease, line-height 0.2s ease',
                      }}>
                        {company.name}
                      </Typography>

                      {/* Details on hover */}
                      <Box className="tile-details" sx={{
                        opacity: 0, transition: 'opacity 0.3s ease 0.08s',
                        display: 'flex', flexDirection: 'column', gap: 0.4, pt: 0.25, pb: 1, flex: 1,
                      }}>
                        <Typography variant="subtitle2" noWrap sx={{ color: '#fff', fontWeight: 700, fontSize: '0.82rem' }}>
                          {company.name}
                        </Typography>
                        {company.companyEmail && (
                          <Typography variant="caption" noWrap sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.72rem' }}>
                            {company.companyEmail}
                          </Typography>
                        )}
                        {location && (
                          <Typography variant="caption" noWrap sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem' }}>
                            {location}
                          </Typography>
                        )}
                        {/* Action buttons */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                          <Box display="flex" gap={0.5}>
                            <Tooltip title="Edit">
                              <Button color="secondary" variant="contained" size="small"
                                onClick={(e) => { e.stopPropagation(); handleEdit(company); }}
                                sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}>
                                <Edit fontSize="small" />
                              </Button>
                            </Tooltip>
                            <Tooltip title="Manage Bot">
                              <Button color="warning" variant="contained" size="small"
                                onClick={(e) => { e.stopPropagation(); handleManageBot(company); }}
                                sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}>
                                <SmartToy fontSize="small" />
                              </Button>
                            </Tooltip>
                          </Box>
                          <Box display="flex" gap={0.5}>
                            <Tooltip title={isDeleteDisabled(company) ? deleteDisabledTooltip(company) : 'Delete'}>
                              <span>
                                <Button color="error" variant="contained" size="small" disabled={isDeleteDisabled(company)}
                                  onClick={(e) => { e.stopPropagation(); handleDelete(company); }}
                                  sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}>
                                  <Delete fontSize="small" />
                                </Button>
                              </span>
                            </Tooltip>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, pt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {paginatedCompanies.length} of {filteredCompanies.length} compan{filteredCompanies.length !== 1 ? 'ies' : 'y'}
              </Typography>
              {hasMoreTiles && (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => setTileVisibleCount(prev => prev + TILE_PAGE_SIZE)}
                >
                  Load More
                </Button>
              )}
            </Box>
          </Box>
        )
      )}

      <CompanyDialog
        open={dialogOpen}
        onClose={closeDialog}
        company={currentCompany}
        isEditing={isEditing}
        onSave={saveCompany}
        saveLoading={saveLoading}
      />

      <CompanyViewDialog
        open={viewDialogOpen}
        onClose={closeViewDialog}
        company={companyToView}
      />

      {/* Manage Bot Dialog */}
      <Dialog open={botManagerDialogOpen} onClose={closeBotManagerDialog} maxWidth="md" fullWidth>
        <DialogTitle>Manage BOT - {companyForBot?.name}</DialogTitle>
        <DialogContent>
          {companyForBot && (
            <CompanyBotManager companyId={companyForBot._id} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeBotManagerDialog} color="secondary" variant="outlined">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {deleteBlockedReason === 'hasUsers' ? 'Cannot Delete Company' : 'Confirm Delete'}
        </DialogTitle>
        <DialogContent>
          {deleteBlockedReason === 'hasUsers' ? (
            <Typography sx={{ pt: 1 }}>
              The company "{companyToDelete?.name}" cannot be deleted because it has users associated with it.
            </Typography>
          ) : (
            <Typography sx={{ pt: 1 }}>
              Are you sure you want to delete the company "{companyToDelete?.name}"? 
              This action cannot be undone.
            </Typography>
          )}
          {companyToDelete?.name === systemCompanyName && (
            <Typography color="error" sx={{ mt: 1 }}>
              Warning: This is the system company and should not be deleted!
            </Typography>
          )}
          {deleteBlockedReason === 'hasUsers' && (
            <Typography color="error" sx={{ mt: 1 }}>
              Cannot delete company that has users. Please remove all users first.
            </Typography>
          )}
          {deleteBlockedReason === 'checkFailed' && (
            <Typography color="error" sx={{ mt: 1 }}>
              Warning: Could not verify if company "{companyToDelete?.name}" has users. Proceed with caution.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={closeDeleteDialog} 
            disabled={deleteLoading}
            color="secondary"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDelete} 
            color="error" 
            variant="contained"
            disabled={deleteLoading || companyToDelete?.name === systemCompanyName || deleteBlockedReason === 'hasUsers'}
          >
            {deleteLoading ? 'Deleting...' : (deleteBlockedReason === 'hasUsers' ? 'Cannot Delete' : 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
